import { HelpCircle, MousePointerClick, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  resetTooltipPrefs,
  setGlobalTooltipPref,
  useGlobalTooltipPrefs,
} from "@/features/tooltips/prefs";

/**
 * Profile-dropdown block for the user's global tooltip preferences.
 * These prefs are account-wide, so the block always renders regardless of the
 * current route. Admin group toggles remain the ceiling for what actually shows.
 */
export function TooltipMenuControls() {
  const { tips, tour } = useGlobalTooltipPrefs();

  return (
    <>
      <DropdownMenuSeparator />
      <PrefRow
        id="tooltips-tips-toggle"
        label="Hover tips"
        icon={<MousePointerClick className="h-4 w-4" aria-hidden="true" />}
        checked={tips}
        onChange={(v) => setGlobalTooltipPref({ tips: v })}
      />
      <PrefRow
        id="tooltips-tour-toggle"
        label="Guided tours"
        icon={<HelpCircle className="h-4 w-4" aria-hidden="true" />}
        checked={tour}
        onChange={(v) => setGlobalTooltipPref({ tour: v })}
      />

      <DropdownMenuItem
        onSelect={() => {
          resetTooltipPrefs();
          toast.success("Tooltips reset — walkthroughs will show again.");
        }}
      >
        <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
        Reset tooltips
      </DropdownMenuItem>
    </>
  );
}

function PrefRow({
  id,
  label,
  icon,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div
      className="flex min-h-[44px] items-center justify-between gap-3 px-2 py-1.5 text-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <label htmlFor={id} className="flex cursor-pointer items-center gap-2">
        {icon}
        {label}
      </label>
      <Switch
        id={id}
        checked={checked}
        aria-label={
          checked
            ? `Turn ${label.toLowerCase()} off`
            : `Turn ${label.toLowerCase()} on`
        }
        onCheckedChange={onChange}
      />
    </div>
  );
}
